import { Typography, Button, Box, Skeleton } from '@mui/material';
import { HiPlus } from 'react-icons/hi';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useProfile, ProfileLink } from 'context/ProfileContext';
import { addLink, deleteLink, editLink, editPosition } from '../../reducer/profileActions';
import Link from './Link/Link';
import './EditableLinks.css';

const LinkSkeleton = () => (
    <div className='editable-link-item editable-link-item--skeleton'>
        <div className='editable-link-main'>
            <Skeleton variant="rounded" width={18} height={26} sx={{ flexShrink: 0 }} />
            <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
            <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
            <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
        </div>
    </div>
);

const EditableLinks = () => {
    const { t } = useTranslation();
    const { state, dispatch, loading } = useProfile();
    const currentLinks = state.currentProfile.links;

    const handleAddLink = () => {
        addLink(dispatch, {
            id_link: parseInt(Math.floor(Math.random() * Date.now()).toString().slice(0, 5)),
            social_url: '',
            position: currentLinks.length > 0 ? Math.max(...currentLinks.map((item: ProfileLink) => item.position)) + 1 : 0,
            social_name: 'link',
            icon_name: 'FaLink',
            icon_id: 13,
        });
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        if (result.type === 'link') {
            editPosition(dispatch, result.destination.index, result.source.index);
        }
    };

    return (
        <div className='editable-links-root'>
            <Box className='editable-links-header'>
                <Box>
                    <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.3 }}>
                        {t('profile.links.title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                        {t('profile.links.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<HiPlus />}
                    onClick={handleAddLink}
                    size="small"
                    disabled={loading}
                    sx={{ flexShrink: 0 }}
                >
                    {t('profile.links.add')}
                </Button>
            </Box>

            <Box className='editable-links-list'>
                {loading ? (
                    <div className='editable-links-droppable'>
                        <LinkSkeleton />
                        <LinkSkeleton />
                        <LinkSkeleton />
                    </div>
                ) : currentLinks.length > 0 ? (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId='dropLink' type='link'>
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} className='editable-links-droppable'>
                                    {currentLinks.map((link: ProfileLink, index: number) => (
                                        <Draggable index={index} draggableId={link.id_link.toString()} key={link.id_link}>
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                    <Link
                                                        link={link}
                                                        allLinks={currentLinks}
                                                        onHandleDnd={provided.dragHandleProps}
                                                        onHandleDeleteLink={(id: number) => deleteLink(dispatch, id)}
                                                        onHandleEditLink={(type, data) => editLink(dispatch, type, data)}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    <Box className='editable-links-empty'>
                        <Typography variant="h6" sx={{ color: 'text.primary' }} className='editable-links-empty-title'>
                            {t('profile.links.empty')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }} className='editable-links-empty-subtitle'>
                            {t('profile.links.empty-subtitle')}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<HiPlus />}
                            onClick={handleAddLink}
                            size="large"
                            className='editable-links-empty-cta'
                        >
                            {t('profile.links.empty-cta')}
                        </Button>
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default EditableLinks;
